import * as client from "../../../client";
import { perClientWithLob } from "../../../client-features";
import { STATE_ABBREV_TO_STATE_NAME } from "../../../datasets";
import {
  Editor,
  MoneyEditor,
  OneLineStringEditor,
  SFHAEditor,
  SicCodeEditor,
  StateEditor,
  YesNoEditor,
} from "./CellEditors";
import { AgEditorName } from "./CellEditorsAg";
import { getIsoConstructionName } from "./construction";
import { SIC_CODES } from "./sic";

export type CustomAction =
  | "open_edit_modal_construction"
  | "open_edit_modal_occupancy"
  | "open_street_view_modal";

export type SovColumnProps = {
  getValue: (data: client.RawAddress) => string | number | undefined | null;
  columnName: string;
  width: number;
  freezeColumn?: boolean;
  field?: keyof client.RawAddress;
  editor?: Editor;
  agEditor?: AgEditorName;
  agEditorParams?: Record<string, unknown>;
  customAction?: CustomAction;
  bulkEdit?: boolean;
  isIconValue?: boolean;
  showTooltipsWhenNotFlagged?: boolean;
  infoText?: string;
  requiredForValidAddress?: boolean;
  renderNumberWithCommas?: boolean;
  filterable?: boolean;
};

const MAP_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => {
    const hasCoords = data.lat && data.long;
    return hasCoords ? "pi pi-map" : null;
  },
  columnName: "Map",
  width: 32,
  isIconValue: true,
  customAction: "open_street_view_modal",
  field: "lat",
  freezeColumn: true,
};
const LOC_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.premises_number,
  columnName: "Loc",
  width: 40,
  field: "premises_number",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  freezeColumn: true,
};
const BLDG_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.building_number,
  columnName: "Bldg",
  width: 40,
  field: "building_number",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  freezeColumn: true,
};
const ADDRESS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.street_address,
  columnName: "Address",
  width: 200,
  field: "street_address",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const CITY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.city,
  columnName: "City",
  width: 150,
  field: "city",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",

  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const STATE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.state,
  columnName: "State",
  width: 85,
  field: "state",
  editor: StateEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: {
    values: Object.keys(STATE_ABBREV_TO_STATE_NAME),
  },
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
  filterable: true,
};
const ZIP_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.zip,
  columnName: "Zip",
  width: 75,
  field: "zip",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const COUNTRY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.country_code,
  columnName: "Country",
  width: 75,
  field: "country_code",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const SFHA_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.flood_zone,
  columnName: "Flood",
  width: 75,
  field: "flood_zone",
  editor: SFHAEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: {
    values: ["", "A", "AE", "AO", "AH", "A99", "VE", "V", "D", "X", "Unknown"],
  },
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
  filterable: true,
};
const COUNTY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.county,
  columnName: "County",
  width: 100,
  field: "county",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  freezeColumn: true,
};
const CONSTRUCTION_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => {
    const isoName = getIsoConstructionName(
      data.construction_code_type,
      data.construction_code
    );
    if (isoName) return isoName;
    if (!data.construction_code_type || !data.construction_code) return "None";
    return `${data.construction_code_type} ${data.construction_code}${
      data.construction_description ? ` (${data.construction_description})` : ""
    }`;
  },
  width: 180,
  columnName: "Construction",
  field: "construction_code",
  customAction: "open_edit_modal_construction",
  filterable: true,
};
const SIC_SELECT_VALUES = [
  "",
  ...Object.entries(SIC_CODES).map(([code, desc]) => `${code} (${desc})`),
];
const SIC_CODE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.sic_code
      ? data.sic_code +
        (data.sic_code in SIC_CODES ? ` (${SIC_CODES[data.sic_code]})` : "")
      : "",
  columnName: "SIC Code",
  width: 280,
  field: "sic_code",
  editor: SicCodeEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: { values: SIC_SELECT_VALUES },
  bulkEdit: true,
  filterable: true,
};
const CLADDING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.ws_cladding_type,
  columnName: "WS Cladding",
  width: 110,
  field: "ws_cladding_type",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const ROOF_COVERING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.roof_covering,
  columnName: "Roof Covering",
  width: 120,
  field: "roof_covering",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const YEAR_BUILT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.year_built,
  columnName: "Year Built",
  width: 110,
  field: "year_built",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};
const STORIES_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.number_of_stories,
  columnName: "Stories",
  width: 90,
  field: "number_of_stories",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};
const SQFT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.square_footage,
  columnName: "Sqft",
  width: 90,
  field: "square_footage",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  renderNumberWithCommas: true,
};
const ITV_SQUARE_FOOTAGE_COLUMN: SovColumnProps = {
  getValue: () => undefined,
  columnName: "ITV",
  width: 80,
};
const VALUE_360_COLUMN: SovColumnProps = {
  getValue: () => undefined,
  columnName: "360 Value",
  width: 125,
};
const getBuildingValueColumn = (columnName: string): SovColumnProps => {
  return {
    getValue: (data: client.RawAddress) =>
      data.building_value ? parseFloat(data.building_value) : undefined,
    columnName: columnName,
    width: 125,
    field: "building_value",
    editor: MoneyEditor,
    agEditor: "agNumberCellEditor",
    bulkEdit: true,
    requiredForValidAddress: true,
  };
};
const BPP_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.bpp_value ? parseFloat(data.bpp_value) : undefined,
  columnName: "BPP",
  width: 125,
  field: "bpp_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const BI_INCL_EE_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.bi_incl_ee_value ? parseFloat(data.bi_incl_ee_value) : undefined,
  columnName: "BI Incl EE",
  width: 125,
  field: "bi_incl_ee_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const BI_EXCL_EE_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.bi_excl_ee_value ? parseFloat(data.bi_excl_ee_value) : undefined,
  columnName: "BI Excl EE",
  width: 125,
  field: "bi_excl_ee_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const TB_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.tenant_betterments_value
      ? parseFloat(data.tenant_betterments_value)
      : undefined,
  columnName: "Tenant Betterments",
  width: 180,
  field: "tenant_betterments_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const FURNITURE_FIXTURES_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.furniture_and_fixtures_value
      ? parseFloat(data.furniture_and_fixtures_value)
      : undefined,
  columnName: "FF",
  width: 100,
  field: "furniture_and_fixtures_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const INVENTORY_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.inventory_value ? parseFloat(data.inventory_value) : undefined,
  columnName: "Inventory",
  width: 100,
  field: "inventory_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const CONTRACTORS_EQUIPMENT_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.contractors_equipment_value
      ? parseFloat(data.contractors_equipment_value)
      : undefined,
  columnName: "Contractor's Equip",
  width: 180,
  field: "contractors_equipment_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const OUTDOOR_PROP_AUTOS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.outdoor_property_and_autos_value
      ? parseFloat(data.outdoor_property_and_autos_value)
      : undefined,
  columnName: "Outdoor Prop & Autos",
  width: 200,
  field: "outdoor_property_and_autos_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const SIGNS_VALUE_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) =>
    data.signs_value ? parseFloat(data.signs_value) : undefined,
  columnName: "Signs",
  width: 100,
  field: "signs_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const getTivColumn = (
  isEditable: boolean,
  infoText: string | undefined
): SovColumnProps => {
  return {
    getValue: (data: client.RawAddress) => data.tiv,
    columnName: "TIV",
    width: 125,
    field: "tiv",
    editor: isEditable ? MoneyEditor : undefined,
    agEditor: isEditable ? "agNumberCellEditor" : undefined,
    infoText: infoText,
    bulkEdit: isEditable,
    requiredForValidAddress: true,
  };
};
const TIV_PER_BUILDING_COLUMN = {
  getValue: (data: client.RawAddress) => {
    const n = data.number_of_buildings && parseFloat(data.number_of_buildings);
    if (n && !isNaN(n) && n > 0) {
      return data.tiv / n;
    }
    return data.tiv;
  },
  columnName: "TIV per Bldg",
  width: 125,
};
const SPRINKLER_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.sprinkler,
  columnName: "Sprinkler",
  width: 90,
  field: "sprinkler",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const FIRE_ALARM_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.fire_alarm_type,
  columnName: "Fire Alarm",
  width: 110,
  field: "fire_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const BURGLAR_ALARM_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.burglar_alarm_type,
  columnName: "Burglar Alarm",
  width: 130,
  field: "burglar_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const ROOFING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.roofing_year,
  columnName: "Roofing",
  width: 100,
  field: "roofing_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const HEATING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.heating_year,
  columnName: "Heating",
  width: 100,
  field: "heating_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const WIRING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.wiring_year,
  columnName: "Wiring",
  width: 100,
  field: "wiring_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const PLUMBING_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.plumbing_year,
  columnName: "Plumbing",
  width: 100,
  field: "plumbing_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const NUMBER_OF_BUILDINGS_COLUMN: SovColumnProps = {
  getValue: (data: client.RawAddress) => data.number_of_buildings,
  columnName: "# of Buildings",
  width: 110,
  field: "number_of_buildings",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};

const BASE_COLUMNS_1 = [MAP_COLUMN, LOC_COLUMN, BLDG_COLUMN];

const LOCATION_COLUMNS = [
  ADDRESS_COLUMN,
  CITY_COLUMN,
  STATE_COLUMN,
  ZIP_COLUMN,
  COUNTY_COLUMN,
  COUNTRY_COLUMN,
  SFHA_COLUMN,
];

const BASE_COLUMNS_2 = [
  CONSTRUCTION_COLUMN,
  SIC_CODE_COLUMN,
  YEAR_BUILT_COLUMN,
  STORIES_COLUMN,
];

const BASE_COLUMNS_3 = ({
  tivColEditable = true,
  tivInfoText,
  whCol,
  itvMultiplierCol,
  itvValueCol,
  tbCol,
  signsCol,
  ffCol,
  invCol,
  ceCol,
  opaCol,
}: {
  tivColEditable?: boolean;
  tivInfoText?: string;
  whCol?: SovColumnProps;
  itvMultiplierCol?: SovColumnProps;
  itvValueCol?: SovColumnProps;
  tbCol?: SovColumnProps;
  signsCol?: SovColumnProps;
  ffCol?: SovColumnProps;
  invCol?: SovColumnProps;
  ceCol?: SovColumnProps;
  opaCol?: SovColumnProps;
}) => [
  ...(itvMultiplierCol ? [itvMultiplierCol] : []),
  ...(itvValueCol ? [itvValueCol] : []),
  getTivColumn(tivColEditable, tivInfoText),
  getBuildingValueColumn("Building"),
  BPP_VALUE_COLUMN,
  BI_INCL_EE_VALUE_COLUMN,
  BI_EXCL_EE_VALUE_COLUMN,
  ...(tbCol ? [tbCol] : []),
  ...(signsCol ? [signsCol] : []),
  ...(ffCol ? [ffCol] : []),
  ...(invCol ? [invCol] : []),
  ...(ceCol ? [ceCol] : []),
  ...(opaCol ? [opaCol] : []),
  ...(whCol ? [whCol] : []),
  SPRINKLER_COLUMN,
];

const BASE_COLUMNS_4 = [
  ROOFING_COLUMN,
  HEATING_COLUMN,
  WIRING_COLUMN,
  PLUMBING_COLUMN,
];

export const getAddressColumns = perClientWithLob<SovColumnProps[]>({
  pantheon: () => [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS,
    ...BASE_COLUMNS_2,
    SQFT_COLUMN,
    VALUE_360_COLUMN,
    ...BASE_COLUMNS_3({
      tivColEditable: false,
      tbCol: TB_VALUE_COLUMN,
      signsCol: SIGNS_VALUE_COLUMN,
      ffCol: FURNITURE_FIXTURES_VALUE_COLUMN,
      invCol: INVENTORY_COLUMN,
      ceCol: CONTRACTORS_EQUIPMENT_COLUMN,
      opaCol: OUTDOOR_PROP_AUTOS_COLUMN,
    }),
    FIRE_ALARM_COLUMN,
    BURGLAR_ALARM_COLUMN,
    ...BASE_COLUMNS_4,
  ],
  luminor: () => [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS,
    ...BASE_COLUMNS_2,
    ITV_SQUARE_FOOTAGE_COLUMN,
    TIV_PER_BUILDING_COLUMN,
    ...BASE_COLUMNS_3({
      tivColEditable: false,
      tbCol: TB_VALUE_COLUMN,
      signsCol: SIGNS_VALUE_COLUMN,
      ffCol: FURNITURE_FIXTURES_VALUE_COLUMN,
      invCol: INVENTORY_COLUMN,
      ceCol: CONTRACTORS_EQUIPMENT_COLUMN,
      opaCol: OUTDOOR_PROP_AUTOS_COLUMN,
    }),
    NUMBER_OF_BUILDINGS_COLUMN,
    ...BASE_COLUMNS_4,
    SQFT_COLUMN,
    CLADDING_COLUMN,
    ROOF_COVERING_COLUMN,
  ],
});
